import { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { elegibilidadeReservaService } from '@/services/elegibilidadeReservaService'
import { FilaGrandesOperacoes as PaginaFila, ReservaGrandesOperacoes, ROTULO_ORIGEM_ROTA } from '@/types/elegibilidadeReserva'
import { STATUS_LABEL, StatusReserva } from '@/types/statusReserva'
import { formatCNPJ, formatCurrency, formatDateTime } from '@/utils/formatters'

/** Status oferecidos no filtro — os não terminais em que a fila costuma ter reservas. */
const STATUS_FILTRO: StatusReserva[] = [
  'ENVIADA',
  'EM_ANALISE_ORIGINEI',
  'COM_PENDENCIA',
  'APROVADA_ORIGINEI',
  'EM_ESTRUTURACAO',
  'EM_NEGOCIACAO',
].filter((s) => s in STATUS_LABEL) as StatusReserva[]

const nomeEmpresa = (r: ReservaGrandesOperacoes): string =>
  r.razaoSocial || r.nomeFantasia || (r.cnpj ? formatCNPJ(r.cnpj) : 'Empresa sem nome')

/**
 * P2 (AN-PP2 §5.3, Q67) — fila de Grandes Operações do back-office: reservas não terminais com
 * rota GRANDES_OPERACOES, mais antigas primeiro (`GET /v1/oportunidades/fila/grandes-operacoes`).
 * O total do rodapé é o `total` da MESMA consulta; o KPI da página usa
 * `Resumo.grandesOperacoes` (count da mesma query, no servidor). Nada é contado na tela.
 * A rota em si é alterada pelo ADMIN no detalhe da reserva (motivo obrigatório).
 */
export const FilaGrandesOperacoes: FC = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState<StatusReserva | ''>('')
  const [pagina, setPagina] = useState<PaginaFila | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<'erro' | 'semPermissao' | null>(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setPagina(await elegibilidadeReservaService.filaGrandesOperacoes({ status: status || null, page: 0 }))
    } catch (e) {
      setErro((e as { status?: number } | null)?.status === 403 ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [status])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const linhas = pagina?.reservas ?? []

  return (
    <Box component="section" aria-label="Fila de Grandes Operações">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Operações de Antecipação a partir do valor de Grandes Operações da política vigente (ou definidas pelo
          Administrador). A rota não muda a fórmula da comissão nem impede o encaminhamento.
        </Typography>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusReserva | '')}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Todos os não terminais</MenuItem>
          {STATUS_FILTRO.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {carregando ? (
        <EstadoDeLista estado="carregando" />
      ) : erro === 'semPermissao' ? (
        <EstadoDeLista estado="semPermissao" />
      ) : erro ? (
        <EstadoDeLista
          estado="erro"
          mensagem="Não foi possível carregar a fila de Grandes Operações."
          acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : linhas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma reserva em Grandes Operações"
          detalhe="Reservas de Antecipação com valor a partir do limite da política aparecem aqui."
        />
      ) : (
        <>
          <TabelaAcao<ReservaGrandesOperacoes>
            aria-label="Reservas em Grandes Operações"
            linhas={linhas}
            chaveDaLinha={(r) => r.id}
            onAbrir={(r) => navigate(`/oportunidades/${r.id}`)}
            renderAcao={() => 'Abrir →'}
            colunas={[
              {
                titulo: 'Empresa',
                render: (r) => (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {nomeEmpresa(r)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.cnpj ? formatCNPJ(r.cnpj) : '—'}
                      {r.numero != null ? ` · RES-${String(r.numero).padStart(4, '0')}` : ''}
                    </Typography>
                  </Box>
                ),
              },
              { titulo: 'Produto', render: (r) => r.tipoNecessidade ?? '—' },
              {
                titulo: 'Valor',
                align: 'right',
                render: (r) => (r.valorEstimado != null ? formatCurrency(r.valorEstimado) : '—'),
              },
              { titulo: 'Status', render: (r) => STATUS_LABEL[r.status] ?? r.status },
              { titulo: 'Consultor', render: (r) => r.consultorNome ?? '—' },
              {
                titulo: 'Rota definida',
                render: (r) => (
                  <Box>
                    <Typography variant="body2">{r.rotaDefinidaEm ? formatDateTime(r.rotaDefinidaEm) : '—'}</Typography>
                    {r.rotaOrigem && (
                      <Typography variant="caption" color="text.secondary">
                        {ROTULO_ORIGEM_ROTA[r.rotaOrigem]}
                      </Typography>
                    )}
                  </Box>
                ),
              },
            ]}
          />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            {pagina?.total ?? linhas.length} reserva(s) na fila
          </Typography>
        </>
      )}
    </Box>
  )
}

export default FilaGrandesOperacoes
